import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem,
  Button, CircularProgress, Chip, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, IconButton, Tooltip, Collapse, Badge, Stack,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { alpha } from '@mui/material/styles';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { orderService } from '../services/orderService';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';

interface Warehouse { id: string; name: string; }

interface CancelledOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  orderType: string;
  itemCount: number;
  marketplaceSource?: string;
  sourceType?: string;
  sourceOrderId?: string;
  cancelReasonCode?: string;
  cancelReasonText?: string;
  cancelNote?: string;
  cancelSource?: string;
  cancelledAt?: string;
  cancelledByName?: string;
  cancelHandlingStatus?: string;
  cancelHandledAt?: string;
  cancelHandledByName?: string;
}

type HandlingFilter = 'Pending' | 'Resolved' | 'all';

const REASON_LABELS: Record<string, string> = {
  stock_missing: 'Ürün bulunamadı',
  stock_mismatch: 'Stok hatalı',
  damaged: 'Hasarlı ürün',
  wrong_product: 'Yanlış ürün',
  barcode_issue: 'Barkod sorunu',
  other: 'Diğer',
};

const SOURCE_LABELS: Record<string, string> = {
  Picking: 'Toplama',
  Pool: 'Sipariş Havuzu',
  Manual: 'Manuel',
  Ecommerce: 'E-ticaret',
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('tr-TR');
  } catch {
    return value;
  }
};

const formatDateShort = (value?: string) => {
  if (!value) return '';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString('tr-TR');
  } catch {
    return value;
  }
};

const WmsCancelManagement: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<CancelledOrder[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [handlingFilter, setHandlingFilter] = useState<HandlingFilter>('Pending');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [cancelledFrom, setCancelledFrom] = useState('');
  const [cancelledTo, setCancelledTo] = useState('');
  const [cancelSource, setCancelSource] = useState('');
  const [appliedCancelledFrom, setAppliedCancelledFrom] = useState('');
  const [appliedCancelledTo, setAppliedCancelledTo] = useState('');
  const [appliedCancelSource, setAppliedCancelSource] = useState('');
  const [resolveTarget, setResolveTarget] = useState<CancelledOrder | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    smartWarehouseService.getAllList()
      .then((list) => {
        const mapped = (list || []).map((w) => ({
          id: w.id,
          name: w.warehouseName,
        }));
        setWarehouses(mapped);
        if (mapped.length > 0) {
          setWarehouseId((prev) => prev || mapped[0].id);
        }
      })
      .catch(() => setWarehouses([]));
  }, []);

  const loadOrders = useCallback(async () => {
    if (!warehouseId) return;
    setLoading(true);
    try {
      const res = await orderService.getByWarehouse(warehouseId, {
        page,
        pageSize: 50,
        status: 'Cancelled',
        cancelQueueOnly: true,
        cancelHandlingStatus: handlingFilter === 'all' ? undefined : handlingFilter,
        search: search.trim() || undefined,
        cancelledFrom: appliedCancelledFrom || undefined,
        cancelledTo: appliedCancelledTo || undefined,
        cancelSource: appliedCancelSource || undefined,
        sortBy: 'cancelledAt',
        sortDir: 'desc',
      });
      const payload = res.data || {};
      const data = payload.data || payload.Data || [];
      // Eski API / eski havuz iptalleri: sebep veya işleme durumu yoksa gösterme.
      const mapped = (Array.isArray(data) ? data : []).map((o: any) => ({
        id: o.id || o.Id,
        orderNumber: o.orderNumber || o.OrderNumber,
        customerName: o.customerName || o.CustomerName,
        orderType: o.orderType || o.OrderType,
        itemCount: o.itemCount ?? o.ItemCount ?? 0,
        marketplaceSource: o.marketplaceSource || o.MarketplaceSource,
        sourceType: o.sourceType || o.SourceType,
        sourceOrderId: o.sourceOrderId || o.SourceOrderId,
        cancelReasonCode: o.cancelReasonCode || o.CancelReasonCode,
        cancelReasonText: o.cancelReasonText || o.CancelReasonText,
        cancelNote: o.cancelNote || o.CancelNote,
        cancelSource: o.cancelSource || o.CancelSource,
        cancelledAt: o.cancelledAt || o.CancelledAt,
        cancelledByName: o.cancelledByName || o.CancelledByName,
        cancelHandlingStatus: o.cancelHandlingStatus || o.CancelHandlingStatus,
        cancelHandledAt: o.cancelHandledAt || o.CancelHandledAt,
        cancelHandledByName: o.cancelHandledByName || o.CancelHandledByName,
      })).filter((o) =>
        o.cancelHandlingStatus === 'Pending'
        || o.cancelHandlingStatus === 'Resolved'
        || !!o.cancelReasonCode
        || !!o.cancelledAt
      );
      setOrders(mapped);
      setTotalCount(
        handlingFilter === 'all' && mapped.length !== (Array.isArray(data) ? data.length : 0)
          ? mapped.length
          : (payload.totalCount ?? payload.TotalCount ?? mapped.length)
      );
    } catch {
      setOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    warehouseId,
    page,
    handlingFilter,
    search,
    appliedCancelledFrom,
    appliedCancelledTo,
    appliedCancelSource,
  ]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedCancelledFrom) count += 1;
    if (appliedCancelledTo) count += 1;
    if (appliedCancelSource) count += 1;
    return count;
  }, [appliedCancelledFrom, appliedCancelledTo, appliedCancelSource]);

  const applyAdvancedFilters = () => {
    setAppliedCancelledFrom(cancelledFrom);
    setAppliedCancelledTo(cancelledTo);
    setAppliedCancelSource(cancelSource);
    setPage(1);
  };

  const clearAdvancedFilters = () => {
    setCancelledFrom('');
    setCancelledTo('');
    setCancelSource('');
    setAppliedCancelledFrom('');
    setAppliedCancelledTo('');
    setAppliedCancelSource('');
    setPage(1);
  };

  const removeAppliedFilter = (key: 'from' | 'to' | 'source') => {
    if (key === 'from') {
      setCancelledFrom('');
      setAppliedCancelledFrom('');
    } else if (key === 'to') {
      setCancelledTo('');
      setAppliedCancelledTo('');
    } else {
      setCancelSource('');
      setAppliedCancelSource('');
    }
    setPage(1);
  };

  const handleResolve = async () => {
    if (!resolveTarget) return;
    setResolving(true);
    try {
      let handledByName = 'Ofis';
      try {
        const details = localStorage.getItem('userDetails');
        if (details) {
          const parsed = JSON.parse(details);
          handledByName = parsed?.displayName || parsed?.name || parsed?.email || handledByName;
        }
      } catch { /* ignore */ }

      await orderService.resolveCancel({
        OrderId: resolveTarget.id,
        HandledByName: handledByName,
        Note: resolveNote.trim() || undefined,
      });
      setResolveTarget(null);
      setResolveNote('');
      await loadOrders();
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.message || 'İşaretleme başarısız');
    } finally {
      setResolving(false);
    }
  };

  const pendingCount = handlingFilter === 'Pending' ? totalCount : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h5" fontWeight={700}>İptal Yönetimi</Typography>
            <GuideButton guideKey="wms.cancelManagement" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Toplama sırasında bildirilen iptal siparişleri. Pazaryeri/iade işlemi ofiste manuel yapılır.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Depo</InputLabel>
            <Select
              label="Depo"
              value={warehouseId}
              onChange={(e) => { setWarehouseId(e.target.value); setPage(1); }}
              startAdornment={<WarehouseIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Yenile">
            <IconButton onClick={loadOrders} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ mb: 2, p: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tabs
            value={handlingFilter}
            onChange={(_, v: HandlingFilter) => { setHandlingFilter(v); setPage(1); }}
            sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0 } }}
          >
            <Tab value="Pending" label={`Onay bekleyen${pendingCount != null ? ` (${pendingCount})` : ''}`} />
            <Tab value="Resolved" label="İşlenen" />
            <Tab value="all" label="Tümü" />
          </Tabs>
          <TextField
            size="small"
            placeholder="Sipariş no / müşteri ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); loadOrders(); } }}
            sx={{ minWidth: 240, ml: 'auto' }}
          />
          <Button variant="outlined" size="small" onClick={() => { setPage(1); loadOrders(); }}>
            Ara
          </Button>
          <Badge badgeContent={activeFilterCount || undefined} color="primary">
            <Button
              variant={showFilters || activeFilterCount > 0 ? 'contained' : 'outlined'}
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters((v) => !v)}
            >
              Gelişmiş Filtre
            </Button>
          </Badge>
        </Box>

        <Collapse in={showFilters}>
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: 'repeat(4, minmax(0, 1fr)) auto',
              },
              alignItems: 'end',
            }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel>Durum</InputLabel>
              <Select
                label="Durum"
                value={handlingFilter}
                onChange={(e) => {
                  setHandlingFilter(e.target.value as HandlingFilter);
                  setPage(1);
                }}
              >
                <MenuItem value="Pending">Onay bekleyen</MenuItem>
                <MenuItem value="Resolved">İşlenen</MenuItem>
                <MenuItem value="all">Tümü</MenuItem>
              </Select>
            </FormControl>
            <AppDatePicker
              size="small"
              fullWidth
              label="İptal başlangıç"
              value={cancelledFrom}
              onChange={setCancelledFrom}
              maxDate={cancelledTo || undefined}
            />
            <AppDatePicker
              size="small"
              fullWidth
              label="İptal bitiş"
              value={cancelledTo}
              onChange={setCancelledTo}
              minDate={cancelledFrom || undefined}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Kaynak</InputLabel>
              <Select
                label="Kaynak"
                value={cancelSource}
                onChange={(e) => setCancelSource(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="Picking">Toplama</MenuItem>
                <MenuItem value="Pool">Sipariş Havuzu</MenuItem>
                <MenuItem value="Manual">Manuel</MenuItem>
                <MenuItem value="Ecommerce">E-ticaret</MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} sx={{ gridColumn: { xs: '1', md: 'auto' } }}>
              <Button variant="contained" size="small" onClick={applyAdvancedFilters}>
                Uygula
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearAdvancedFilters}
                disabled={activeFilterCount === 0 && !cancelledFrom && !cancelledTo && !cancelSource}
              >
                Temizle
              </Button>
            </Stack>
          </Box>
        </Collapse>

        {activeFilterCount > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {appliedCancelledFrom && (
              <Chip
                size="small"
                label={`Başlangıç: ${formatDateShort(appliedCancelledFrom)}`}
                onDelete={() => removeAppliedFilter('from')}
              />
            )}
            {appliedCancelledTo && (
              <Chip
                size="small"
                label={`Bitiş: ${formatDateShort(appliedCancelledTo)}`}
                onDelete={() => removeAppliedFilter('to')}
              />
            )}
            {appliedCancelSource && (
              <Chip
                size="small"
                label={`Kaynak: ${SOURCE_LABELS[appliedCancelSource] || appliedCancelSource}`}
                onDelete={() => removeAppliedFilter('source')}
              />
            )}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : orders.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">İptal kaydı bulunamadı.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.08) }}>
                <TableCell>Sipariş</TableCell>
                <TableCell>Müşteri</TableCell>
                <TableCell>Kanal</TableCell>
                <TableCell>Sebep</TableCell>
                <TableCell>Kaynak</TableCell>
                <TableCell>İptal Eden</TableCell>
                <TableCell>Tarih</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell align="right">İşlem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{o.orderNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.orderType} · {o.itemCount} kalem
                    </Typography>
                  </TableCell>
                  <TableCell>{o.customerName}</TableCell>
                  <TableCell>{o.marketplaceSource || o.sourceType || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {o.cancelReasonText || REASON_LABELS[o.cancelReasonCode || ''] || o.cancelReasonCode || '-'}
                    </Typography>
                    {o.cancelNote && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 220 }}>
                        {o.cancelNote}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{SOURCE_LABELS[o.cancelSource || ''] || o.cancelSource || '-'}</TableCell>
                  <TableCell>{o.cancelledByName || '-'}</TableCell>
                  <TableCell>{formatDate(o.cancelledAt)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={o.cancelHandlingStatus === 'Resolved' ? 'success' : 'warning'}
                      label={o.cancelHandlingStatus === 'Resolved' ? 'İşlendi' : 'Onay bekliyor'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {o.cancelHandlingStatus !== 'Resolved' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={() => setResolveTarget(o)}
                      >
                        Onayla
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {totalCount > 50 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Önceki</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>Sayfa {page}</Typography>
          <Button disabled={page * 50 >= totalCount} onClick={() => setPage((p) => p + 1)}>Sonraki</Button>
        </Box>
      )}

      <Dialog open={!!resolveTarget} onClose={() => !resolving && setResolveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>İptal onayla</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Sipariş: <strong>{resolveTarget?.orderNumber}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pazaryeri iptal/iade işlemini tamamladıysanız onaylayın; kayıt işlenenlere taşınır.
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="İşlem notu (opsiyonel)"
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveTarget(null)} disabled={resolving}>Vazgeç</Button>
          <Button variant="contained" color="success" onClick={handleResolve} disabled={resolving}>
            {resolving ? <CircularProgress size={18} /> : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmsCancelManagement;
